import { z } from "zod";
import type { ComicIndex, ComicIssue, NewsFeed, NewsIndex } from "./types";

const sourceSchema = z.object({
  title: z.string(),
  url: z.string(),
});

const mediaVariantSchema = z.object({
  url: z.string(),
  width: z.number().positive(),
  type: z.enum(["image/avif", "image/webp"]),
});

const articleMediaSchema = z.object({
  url: z.string(),
  alt: z.string().default("Ilustracja redakcyjna"),
  kind: z.enum(["ai-illustration", "source-image"]).default("ai-illustration"),
  label: z.string().default("Ilustracja redakcyjna AI"),
  generatedAt: z.string().default(""),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  variants: z.array(mediaVariantSchema).optional(),
});

const articleSchema = z.object({
  id: z.string(),
  category: z.enum(["AI", "Technologia", "Polska", "Świat"]),
  section: z.string().default("Wiadomości"),
  title: z.string(),
  summary: z.string().default(""),
  whatHappened: z.string().default(""),
  whyItMatters: z.string().default(""),
  analysis: z.array(z.string()).default([]),
  watchNext: z.array(z.string()).default([]),
  sources: z.array(sourceSchema).default([]),
  tags: z.array(z.string()).default([]),
  priority: z.enum(["high", "medium", "low"]).default("medium"),
  publishedAt: z.string().default("—"),
  sourceTopic: z.string().default("news"),
  keyFacts: z.array(z.string()).optional(),
  background: z.array(z.string()).optional(),
  media: articleMediaSchema.optional(),
  relatedIds: z.array(z.string()).optional(),
  editorial: z.object({
    aiAssisted: z.boolean(),
    verifiedAt: z.string(),
    sourceCount: z.number(),
    revision: z.string(),
    inputFingerprint: z.string().optional(),
  }).optional(),
}).passthrough();

export const newsFeedSchema = z.object({
  generatedAt: z.string(),
  runDate: z.string(),
  runTime: z.string(),
  articles: z.array(articleSchema),
  revision: z.string().optional(),
  featuredArticleId: z.string().optional(),
  sectionOrder: z.array(z.enum(["briefing", "technology", "world", "appPromo"])).optional(),
}).passthrough();

const editionSchema = z.object({
  id: z.string(),
  topic: z.enum(["puls-dnia-news", "polska-swiat", "tech-news", "openclaw-news"]),
  label: z.string(),
  runDate: z.string(),
  runTime: z.string(),
  articleCount: z.number(),
  sourceFile: z.string(),
  modifiedAt: z.string(),
  url: z.string(),
});

export const newsIndexSchema = z.object({
  generatedAt: z.string(),
  editions: z.array(editionSchema),
});

const comicEditionSchema = z.object({
  id: z.string(),
  number: z.number(),
  date: z.string(),
  title: z.string(),
  deck: z.string(),
  pageCount: z.number(),
  storyCount: z.number(),
  generatedAt: z.string(),
  pdfUrl: z.string(),
  issueUrl: z.string().optional(),
  coverImageUrl: z.string().optional(),
  fingerprint: z.string(),
  articleFingerprints: z.array(z.string()),
});

export const comicIndexSchema = z.object({
  generatedAt: z.string(),
  latest: comicEditionSchema.nullable(),
  editions: z.array(comicEditionSchema),
});

const comicImageSchema = z.object({
  role: z.enum(["hero", "support-1", "support-2"]),
  url: z.string(),
  alt: z.string(),
});

const comicStorySchema = z.object({
  articleId: z.string(),
  number: z.number(),
  category: z.enum(["AI", "Technologia", "Polska", "Świat"]),
  title: z.string(),
  subtitle: z.string(),
  setup: z.string(),
  dialogue: z.array(z.string()),
  punchline: z.string(),
  fact: z.string(),
  context: z.string(),
  originalTitle: z.string(),
  imageUrl: z.string().optional(),
  images: z.array(comicImageSchema).optional(),
  sources: z.array(sourceSchema),
});

export const comicIssueSchema = z.object({
  schemaVersion: z.literal("1.0"),
  id: z.string(),
  date: z.string(),
  number: z.number(),
  generatedAt: z.string(),
  coverTitle: z.string(),
  coverDeck: z.string(),
  coverImageUrl: z.string().optional(),
  editorialNote: z.string(),
  stories: z.array(comicStorySchema),
});

export type ParsedData = {
  feed: NewsFeed;
  newsIndex: NewsIndex;
  comicIndex: ComicIndex;
  comicIssue: ComicIssue;
};
